import { useTranslation } from 'react-i18next'

import { FunnelSimpleIcon, PlusIcon, XIcon } from '@phosphor-icons/react'

import { Button, CardTitle, Drawer } from '@reveni-io/reveni-ui'

import useFiltersDrawer from '@/common/hooks/useFiltersDrawer/useFiltersDrawer'

import FilterChips from './FilterChips/FilterChips'
import FilterRow from './FilterRow/FilterRow'
import { getAvailableFilterTypes } from './helpers'
import * as Styles from './styles'

const FiltersDrawer = ({
  show,
  onClose,
  onApply,
  onClearFilters,
  appliedFilters = {},
  filterTypes = [],
  eligibleFilterTypes,
  sections,
  getInitialFilters,
  getDefaultFilterValue,
  getFilterLabel,
  isFilterValid,
  filterFields,
  fieldProps,
  chipContext,
}) => {
  const { t, i18n } = useTranslation()
  const {
    filters,
    cardRefs,
    hasActiveFilters,
    isApplyDisabled,
    handleAddFilter,
    handleDeleteFilter,
    handleChangeType,
    handleChangeValue,
    handleResetFilters,
    handleClickChip,
    restoreSnapshot,
  } = useFiltersDrawer({
    show,
    appliedFilters,
    getInitialFilters,
    getDefaultFilterValue,
    isFilterValid,
  })

  const dropdownBase = eligibleFilterTypes || filterTypes

  const handleApply = () => {
    const activeFilters = filters.filter(f => f.type)
    onClearFilters()
    onApply(activeFilters)
    onClose()
  }

  const handleCancel = () => {
    restoreSnapshot()
    onClose()
  }

  const registerCardRef = id => el => {
    cardRefs.current[id] = el
  }

  return (
    <Drawer show={show} onClose={handleCancel} options={{ showHeaderActions: false }}>
      <CardTitle Icon={FunnelSimpleIcon}>{t('Filtros')}</CardTitle>

      <Styles.Container>
        {hasActiveFilters && (
          <Styles.SummarySection>
            <FilterChips
              filters={filters}
              onClickChip={handleClickChip}
              onDeleteChip={handleDeleteFilter}
              context={{ t, language: i18n.language, ...chipContext }}
              isFilterValid={isFilterValid}
            />
            <Styles.ResetButtonContainer>
              <Button variant="text" size="s" onClick={handleResetFilters}>
                <XIcon size={20} weight="light" />
                {t('Limpiar filtros')}
              </Button>
            </Styles.ResetButtonContainer>
          </Styles.SummarySection>
        )}

        {hasActiveFilters && <Styles.Divider />}

        <Styles.FiltersSection>
          {filters.map((filter, index) => (
            <FilterRow
              key={filter.id}
              filter={filter}
              showSeparator={index > 0}
              isDeletable={filters.length > 1 || Boolean(filter.type)}
              availableFilterTypes={getAvailableFilterTypes(filters, filter.id, dropdownBase)}
              label={getFilterLabel(filter)}
              registerRef={registerCardRef(filter.id)}
              sections={sections}
              filterFields={filterFields}
              fieldProps={fieldProps}
              onChangeType={handleChangeType}
              onChangeValue={handleChangeValue}
              onDelete={handleDeleteFilter}
            />
          ))}
        </Styles.FiltersSection>

        <Styles.AddFilterContainer>
          <Button variant="text" size="s" onClick={handleAddFilter}>
            <PlusIcon size={24} />
            {t('Añadir filtro')}
          </Button>
        </Styles.AddFilterContainer>

        <Styles.Divider />

        <Styles.ActionsContainer>
          <Button onClick={handleCancel} variant="outlined">
            {t('Cancelar')}
          </Button>
          <Button onClick={handleApply} disabled={isApplyDisabled}>
            {t('Aplicar filtros')}
          </Button>
        </Styles.ActionsContainer>
      </Styles.Container>
    </Drawer>
  )
}

export default FiltersDrawer
